import React, { FC } from 'react'
import classNames from 'classnames'
import { ExternalLink } from '@/atoms/ExternalLink'
import { FlexColumn, FlexRow } from '@/atoms/FlexContainers'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { Image } from '@/atoms/Image'
import { Lozenge } from '@/atoms/Lozenge'
import { ResponsiveBackgroundImage } from '@/atoms/ResponsiveBackgroundImage'
import { Footer, HeadingSM, LabelMD, ParagraphSM } from '@/atoms/Text'
import { slugs } from '@/constants/slugs'
import { Modal } from '@/molecules/Modal'
import { useGuildMember } from '@/services/GuildUserService'
import { useUser } from '@/services/UserService'
import { useSafeTrack } from '@/utils/analytics'
import { Translate, useTranslate } from '@/utils/translate/translate-client'

const FOCUS_RESET_CLASSES =
  'focus:border-none focus:outline-none focus:ring-0 focus-visible:border-none focus-visible:outline-none focus-visible:ring-0'

interface Props {
  theatricalSlug: string
  isOpen: boolean
  onClose: () => void
}
export const LimitedDateModal: FC<Props> = ({ theatricalSlug, isOpen, onClose }) => {
  const { t } = useTranslate('tickets')
  const track = useSafeTrack()
  const { isLoggedIn } = useUser()
  const { isGuildMember, loading: loadingGuildMember } = useGuildMember()

  const handleJoinGuildClick = () => {
    track('Join the Angel Guild Button Clicked')
  }
  const handleCurrentMemberClick = () => {
    track('Im A Guild Member Button Clicked')
  }

  if (theatricalSlug === slugs.david) {
    return (
      <Modal
        backgroundScreenColor="black"
        isOpen={isOpen}
        onClose={onClose}
        panelClasses="w-[calc(100vw-32px)] mx-auto !max-w-[396px] md:!max-w-[520px] !overflow-visible !bg-black/20 shadow-dark-4 rounded-2xl md:rounded-3xl !p-0 relative text-left"
        transition="appear"
        className="z-[99999]"
        childWrapperClassName="!m-0"
      >
        <div
          className="absolute right-6 top-6 z-50 cursor-pointer rounded-full bg-white/20 p-[10px] backdrop-blur-[24px]"
          onClick={onClose}
        >
          <CloseIcon color="white" size={20} />
        </div>

        <div className="relative top-0 z-10">
          <FlexColumn className="min-h-full overflow-hidden rounded-t-2xl md:rounded-t-3xl">
            <Image
              src="/v1762368801/theatrical/david/david-red-carpet-img-04"
              alt=""
              height={260}
              width={520}
              className="h-[200px] object-cover md:h-[260px]"
            />

            <FlexRow className="relative overflow-hidden rounded-b-2xl md:rounded-b-3xl">
              <ResponsiveBackgroundImage src="/v1762306629/david/david-premiere-bg" />
              <div className="inset-shadow-sm relative">
                <FlexColumn className="flex-1 gap-6 p-6 md:gap-8 md:p-10">
                  <FlexColumn className="gap-3">
                    <Lozenge size="sm" variant="guild" className="md:lozenge-md">
                      {t('guildMembersOnlyNoGirlsAllowed', 'Guild Members only')}
                    </Lozenge>
                    <HeadingSM weight="semibold" color="white" className="md:photon-heading-md">
                      {t('vipPremiereEvent', 'VIP Premiere Event')}
                    </HeadingSM>
                  </FlexColumn>

                  <ParagraphSM weight="medium" color="white" className="md:photon-paragraph-md">
                    <Translate i18nKey="exclusiveInviteTextDavid" t={t}>
                      Angel Guild Members are exclusively invited to join Phil Wickham and Phil Cunningham on{' '}
                      <strong>December 8, 2025</strong> in Lehi, Utah for the World Premiere of DAVID.
                    </Translate>
                  </ParagraphSM>

                  {!loadingGuildMember && (
                    <FlexRow className="flex-col gap-2 md:flex-row">
                      {(isGuildMember || !isLoggedIn) && (
                        <ExternalLink
                          className={classNames(
                            'flex h-12 w-full flex-col items-center justify-center gap-[2px] rounded-xl bg-[#EA6F13] px-0 py-2',
                            FOCUS_RESET_CLASSES,
                          )}
                          href="https://shop.angel.com/products/david-premiere"
                          onClick={handleCurrentMemberClick}
                        >
                          {!isLoggedIn && (
                            <Footer italic color="white" weight="normal">
                              {t('imAGuildMember', "I'm a Guild Member")}
                            </Footer>
                          )}
                          <LabelMD weight="medium" color="white">
                            {t('buyTickets', 'Buy Tickets')}
                          </LabelMD>
                        </ExternalLink>
                      )}
                      {!isGuildMember && (
                        <ExternalLink
                          className={classNames(
                            'flex h-12 w-full flex-col items-center justify-center gap-[2px] rounded-xl bg-white/20 px-0 py-2 backdrop-blur-[24px]',
                            FOCUS_RESET_CLASSES,
                          )}
                          href="https://angel.com/guild/join"
                          onClick={handleJoinGuildClick}
                        >
                          <Footer italic color="white" weight="normal">
                            {t('iWantToGoToTheRedCarpet', 'I want to go to the Red Carpet')}
                          </Footer>
                          <LabelMD weight="medium" color="white">
                            {t('joinTheAngelGuild', 'Join the Angel Guild')}
                          </LabelMD>
                        </ExternalLink>
                      )}
                    </FlexRow>
                  )}
                </FlexColumn>
              </div>
            </FlexRow>
          </FlexColumn>
        </div>
      </Modal>
    )
  }

  return null
}
